// SEO Analytics and Monitoring for MaxiCar Worms
// Comprehensive tracking system for SEO performance and ranking monitoring

export const seoAnalytics = {
  // Core Web Vitals Tracking
  trackCoreWebVitals: () => {
    if ('PerformanceObserver' in window) {
      // Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        const lcpValue = lastEntry.renderTime || lastEntry.loadTime;
        
        console.log('LCP:', lcpValue);
        
        // Send to analytics
        if (window.gtag) {
          window.gtag('event', 'LCP', {
            event_category: 'Web Vitals',
            value: Math.round(lcpValue),
            custom_parameter_1: window.location.pathname
          });
        }
        
        // Store for SEO monitoring
        seoAnalytics.storeMetric('lcp', lcpValue);
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          const fidValue = entry.processingStart - entry.startTime;
          console.log('FID:', fidValue);
          
          if (window.gtag) {
            window.gtag('event', 'FID', {
              event_category: 'Web Vitals',
              value: Math.round(fidValue),
              custom_parameter_1: window.location.pathname
            });
          }
          
          seoAnalytics.storeMetric('fid', fidValue);
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift (CLS)
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        console.log('CLS:', clsValue);
        
        if (window.gtag) {
          window.gtag('event', 'CLS', {
            event_category: 'Web Vitals',
            value: Math.round(clsValue * 1000),
            custom_parameter_1: window.location.pathname
          });
        }
        
        seoAnalytics.storeMetric('cls', clsValue);
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    }
  },

  // SEO Performance Metrics
  trackSEOPerformance: () => {
    // Page load time
    window.addEventListener('load', () => {
      const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
      console.log('Page load time:', loadTime + 'ms');
      
      if (window.gtag) {
        window.gtag('event', 'page_load_time', {
          event_category: 'SEO Performance',
          value: loadTime,
          custom_parameter_1: window.location.pathname
        });
      }
      
      seoAnalytics.storeMetric('page_load_time', loadTime);
    });

    // DOM content loaded
    window.addEventListener('DOMContentLoaded', () => {
      const domLoadTime = performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart;
      console.log('DOM load time:', domLoadTime + 'ms');
      
      seoAnalytics.storeMetric('dom_load_time', domLoadTime);
    });

    // Time to interactive
    window.addEventListener('load', () => {
      const tti = performance.timing.domInteractive - performance.timing.navigationStart;
      console.log('Time to Interactive:', tti + 'ms');
      
      seoAnalytics.storeMetric('time_to_interactive', tti);
    });
  },

  // Search Engine Ranking Tracking
  trackSearchRankings: () => {
    // Track organic search traffic
    const referrer = document.referrer;
    if (referrer && (referrer.includes('google.') || referrer.includes('bing.') || referrer.includes('yahoo.'))) {
      const searchEngine = referrer.includes('google.') ? 'google' : 
                         referrer.includes('bing.') ? 'bing' : 'yahoo';
      
      if (window.gtag) {
        window.gtag('event', 'organic_search', {
          event_category: 'SEO Traffic',
          event_label: searchEngine,
          custom_parameter_1: seoAnalytics.getSearchKeyword(referrer)
        });
      }
      
      console.log('Organic search detected:', searchEngine);
    }
  },

  // Keyword Tracking
  getSearchKeyword: (referrer) => {
    try {
      const url = new URL(referrer);
      const searchParams = url.searchParams;
      
      if (referrer.includes('google.')) {
        return searchParams.get('q') || 'unknown';
      } else if (referrer.includes('bing.')) {
        return searchParams.get('q') || 'unknown';
      } else if (referrer.includes('yahoo.')) {
        return searchParams.get('p') || 'unknown';
      }
    } catch (error) {
      console.error('Error parsing search keyword:', error);
    }
    return 'unknown';
  },

  // Local SEO Tracking
  trackLocalSEO: () => {
    // Track location-based searches
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          
          if (window.gtag) {
            window.gtag('event', 'local_search', {
              event_category: 'Local SEO',
              custom_parameter_1: latitude,
              custom_parameter_2: longitude,
              custom_parameter_3: window.location.pathname
            });
          }
          
          console.log('Location tracked:', latitude, longitude);
        },
        (error) => {
          console.log('Geolocation denied or unavailable');
        },
        { timeout: 5000 }
      );
    }
  },

  // Content Performance Tracking
  trackContentPerformance: () => {
    // Track scroll depth
    let maxScrollDepth = 0;
    window.addEventListener('scroll', () => {
      const scrollDepth = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );
      maxScrollDepth = Math.max(maxScrollDepth, scrollDepth);
    });

    // Track time on page
    const startTime = Date.now();
    window.addEventListener('beforeunload', () => {
      const timeOnPage = Date.now() - startTime;
      const timeOnPageSeconds = Math.round(timeOnPage / 1000);
      
      if (window.gtag) {
        window.gtag('event', 'time_on_page', {
          event_category: 'Content Engagement',
          value: timeOnPageSeconds,
          custom_parameter_1: window.location.pathname,
          custom_parameter_2: maxScrollDepth
        });
      }
      
      console.log('Time on page:', timeOnPageSeconds + 's');
      console.log('Max scroll depth:', maxScrollDepth + '%');
    });

    // Track click events
    document.addEventListener('click', (event) => {
      const target = event.target;
      const tagName = target.tagName.toLowerCase();
      const className = target.className;
      const text = target.textContent?.substring(0, 50);
      
      if (window.gtag) {
        window.gtag('event', 'content_click', {
          event_category: 'Content Engagement',
          event_label: `${tagName}:${className}:${text}`,
          custom_parameter_1: window.location.pathname
        });
      }
    });
  },

  // Conversion Tracking
  trackConversions: () => {
    // Track booking conversions
    const bookingButtons = document.querySelectorAll('[data-booking-conversion]');
    bookingButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (window.gtag) {
          window.gtag('event', 'booking_conversion', {
            event_category: 'Conversions',
            value: 1,
            custom_parameter_1: window.location.pathname
          });
        }
        
        console.log('Booking conversion tracked');
      });
    });

    // Track phone call conversions
    const phoneLinks = document.querySelectorAll('a[href^="tel:"]');
    phoneLinks.forEach(link => {
      link.addEventListener('click', () => {
        if (window.gtag) {
          window.gtag('event', 'phone_call_conversion', {
            event_category: 'Conversions',
            value: 1,
            custom_parameter_1: window.location.pathname
          });
        }
        
        console.log('Phone call conversion tracked');
      });
    });

    // Track form submissions
    const forms = document.querySelectorAll('form[data-conversion-tracking]');
    forms.forEach(form => {
      form.addEventListener('submit', () => {
        if (window.gtag) {
          window.gtag('event', 'form_conversion', {
            event_category: 'Conversions',
            value: 1,
            custom_parameter_1: window.location.pathname
          });
        }
        
        console.log('Form conversion tracked');
      });
    });
  },

  // SEO Health Monitoring
  monitorSEOHealth: () => {
    // Check for missing alt tags
    const images = document.querySelectorAll('img');
    const imagesWithoutAlt = Array.from(images).filter(img => !img.alt);
    if (imagesWithoutAlt.length > 0) {
      console.warn('Images without alt tags:', imagesWithoutAlt.length);
      seoAnalytics.storeMetric('images_without_alt', imagesWithoutAlt.length);
    }

    // Check for missing title tags
    if (!document.title || document.title.length === 0) {
      console.warn('Missing title tag');
      seoAnalytics.storeMetric('missing_title', 1);
    }

    // Check for missing meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription || !metaDescription.content) {
      console.warn('Missing meta description');
      seoAnalytics.storeMetric('missing_meta_description', 1);
    }

    // Check heading structure
    const _headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const h1Count = document.querySelectorAll('h1').length;
    if (h1Count === 0 || h1Count > 1) {
      console.warn('Invalid H1 structure:', h1Count);
      seoAnalytics.storeMetric('invalid_h1_structure', h1Count);
    }

    // Check internal links
    const internalLinks = document.querySelectorAll('a[href^="/"], a[href^="' + window.location.origin + '"]');
    console.log('Internal links found:', internalLinks.length);
    seoAnalytics.storeMetric('internal_links', internalLinks.length);

    // Check external links
    const externalLinks = document.querySelectorAll('a[href^="http"]:not([href^="' + window.location.origin + '"])');
    console.log('External links found:', externalLinks.length);
    seoAnalytics.storeMetric('external_links', externalLinks.length);
  },

  // Store metrics locally for analysis
  storeMetric: (metricName, value) => {
    const metrics = JSON.parse(localStorage.getItem('seo_metrics') || '{}');
    const timestamp = new Date().toISOString();
    
    if (!metrics[metricName]) {
      metrics[metricName] = [];
    }
    
    metrics[metricName].push({
      value,
      timestamp,
      url: window.location.pathname
    });
    
    // Keep only last 100 entries per metric
    if (metrics[metricName].length > 100) {
      metrics[metricName] = metrics[metricName].slice(-100);
    }
    
    localStorage.setItem('seo_metrics', JSON.stringify(metrics));
  },

  // Get stored metrics
  getStoredMetrics: () => {
    return JSON.parse(localStorage.getItem('seo_metrics') || '{}');
  },

  // Generate SEO report
  generateSEOReport: () => {
    const metrics = seoAnalytics.getStoredMetrics();
    const report = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      metrics: {},
      recommendations: []
    };

    // Analyze Core Web Vitals
    if (metrics.lcp && metrics.lcp.length > 0) {
      const avgLCP = metrics.lcp.reduce((sum, item) => sum + item.value, 0) / metrics.lcp.length;
      report.metrics.lcp = {
        average: Math.round(avgLCP),
        latest: metrics.lcp[metrics.lcp.length - 1].value,
        status: avgLCP < 2500 ? 'good' : avgLCP < 4000 ? 'needs_improvement' : 'poor'
      };
      
      if (avgLCP > 2500) {
        report.recommendations.push('Optimize images and reduce server response time to improve LCP');
      }
    }

    if (metrics.fid && metrics.fid.length > 0) {
      const avgFID = metrics.fid.reduce((sum, item) => sum + item.value, 0) / metrics.fid.length;
      report.metrics.fid = {
        average: Math.round(avgFID),
        latest: metrics.fid[metrics.fid.length - 1].value,
        status: avgFID < 100 ? 'good' : avgFID < 300 ? 'needs_improvement' : 'poor'
      };
      
      if (avgFID > 100) {
        report.recommendations.push('Reduce JavaScript execution time and main thread work to improve FID');
      }
    }

    if (metrics.cls && metrics.cls.length > 0) {
      const avgCLS = metrics.cls.reduce((sum, item) => sum + item.value, 0) / metrics.cls.length;
      report.metrics.cls = {
        average: Math.round(avgCLS * 1000) / 1000,
        latest: metrics.cls[metrics.cls.length - 1].value,
        status: avgCLS < 0.1 ? 'good' : avgCLS < 0.25 ? 'needs_improvement' : 'poor'
      };
      
      if (avgCLS > 0.1) {
        report.recommendations.push('Ensure proper image dimensions and avoid layout shifts to improve CLS');
      }
    }

    // Analyze page performance
    if (metrics.page_load_time && metrics.page_load_time.length > 0) {
      const avgLoadTime = metrics.page_load_time.reduce((sum, item) => sum + item.value, 0) / metrics.page_load_time.length;
      report.metrics.page_load_time = {
        average: Math.round(avgLoadTime),
        latest: metrics.page_load_time[metrics.page_load_time.length - 1].value,
        status: avgLoadTime < 2000 ? 'good' : avgLoadTime < 3000 ? 'needs_improvement' : 'poor'
      };
      
      if (avgLoadTime > 2000) {
        report.recommendations.push('Optimize resources and enable compression to improve page load time');
      }
    }

    // SEO health metrics
    if (metrics.images_without_alt && metrics.images_without_alt.length > 0) {
      const latestAltIssue = metrics.images_without_alt[metrics.images_without_alt.length - 1];
      report.metrics.images_without_alt = {
        latest: latestAltIssue.value,
        status: latestAltIssue.value > 0 ? 'needs_fix' : 'good'
      };
      
      if (latestAltIssue.value > 0) {
        report.recommendations.push('Add descriptive alt text to all images for better SEO and accessibility');
      }
    }

    if (metrics.internal_links && metrics.internal_links.length > 0) {
      const latestInternalLinks = metrics.internal_links[metrics.internal_links.length - 1];
      report.metrics.internal_links = {
        latest: latestInternalLinks.value,
        status: latestInternalLinks.value > 10 ? 'good' : 'needs_improvement'
      };
      
      if (latestInternalLinks.value < 10) {
        report.recommendations.push('Add more internal links to improve site structure and SEO');
      }
    }

    return report;
  },

  // Send SEO data to backend
  sendSEOData: async () => {
    const report = seoAnalytics.generateSEOReport();
    
    try {
      const response = await fetch('/api/seo-analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(report)
      });
      
      if (response.ok) {
        console.log('SEO data sent successfully');
      } else {
        console.error('Failed to send SEO data');
      }
    } catch (error) {
      console.error('Error sending SEO data:', error);
    }
  },

  // Initialize all SEO tracking
  initialize: () => {
    console.log('Initializing SEO Analytics...');
    
    // Initialize all tracking modules
    seoAnalytics.trackCoreWebVitals();
    seoAnalytics.trackSEOPerformance();
    seoAnalytics.trackSearchRankings();
    seoAnalytics.trackLocalSEO();
    seoAnalytics.trackContentPerformance();
    seoAnalytics.trackConversions();
    
    // Monitor SEO health after page load
    window.addEventListener('load', () => {
      setTimeout(() => {
        seoAnalytics.monitorSEOHealth();
      }, 2000);
    });

    // Send SEO data periodically
    setInterval(() => {
      seoAnalytics.sendSEOData();
    }, 300000); // Every 5 minutes

    console.log('SEO Analytics initialized successfully');
  }
};

// Export for use in components
export default seoAnalytics;
